import { Component, OnInit, OnDestroy, inject, DestroyRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanyGroup } from '@shared/entities/companyGroup';
import { CompanyGroupService } from '@shared/services/api/company-group.service';
import { Paginate } from '@shared/entities/paginate';
import { ThemeService } from '../services/theme.service';
import { FilterSelectedEventType } from '@shared/models/types';

@Component({
  selector: 'app-company-group',
  templateUrl: './company-group.component.html',
  styleUrl: './company-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyGroupComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _companyGroupService = inject(CompanyGroupService);
  private _router = inject(Router);

  isLight = true;
  allCompanyGroups$: Observable<CompanyGroup[]>;
  emptyFilters$ = of({});

  prevPage?: number;
  currentPage = 1;
  nextPage?: number;
  totalPages?: number;

  constructor() {
    this.allCompanyGroups$ = this._companyGroupService.allCompanyGroups$.pipe(
      switchMap((paginated: Paginate<CompanyGroup>) => {
        this.prevPage = paginated.prevPage;
        this.currentPage = paginated.currentPage ?? 1;
        this.nextPage = paginated.nextPage;
        this.totalPages = paginated.totalPage;
        this._cdr.markForCheck();
        return of(paginated.data);
      })
    );
  }

  ngOnInit(): void {
    this._companyGroupService.refresh(1).subscribe();

    this._themeService.isLight$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isLight) => {
      this.isLight = isLight;
      this._cdr.markForCheck();
    });
  }

  onFilterSelected(filters: FilterSelectedEventType[]): void {
    const nameFilter = filters.find(f => f.name === 'name');
    this._companyGroupService.filters = nameFilter?.selectedValue
      ? { name: nameFilter.selectedValue }
      : {};
  }

  onPageSelected(page: number): void {
    this._companyGroupService.refresh(page).pipe(
      tap((paginated) => {
        this.prevPage = paginated.prevPage;
        this.currentPage = paginated.currentPage ?? 1;
        this.nextPage = paginated.nextPage;
        this.totalPages = paginated.totalPage;
        this._cdr.markForCheck();
      })
    ).subscribe();
  }

  goBack(): void {
    this._router.navigate(['/user']);
  }

  getYear(): number {
    return new Date().getFullYear();
  }

  ngOnDestroy(): void {
    this._companyGroupService.refreshFilters();
  }
}
